// Registration step 1: toggle organisation fields based on Membership Type.
// International Affiliates provide a Country of Registration + Company
// Registration Number; all other (Australian) entities provide an ABN + ANZSIC
// code. Hidden fields are disabled so they aren't submitted or block HTML5
// `required` validation.
import { Controller } from 'stimulus'

const INTERNATIONAL_AFFILIATE = 'International_Affiliate'

export default class extends Controller {
  static targets = [
    'membership_type',
    'country',
    'country_wrapper',
    'abn',
    'abn_wrapper',
    'company_reg',
    'company_reg_wrapper',
    'anzsic',
    'anzsic_wrapper',
  ]

  updateFields() {
    const isInternational =
      this.membership_typeTarget.value === INTERNATIONAL_AFFILIATE

    // International Affiliate fields
    this.toggle(this.countryTarget, this.country_wrapperTarget, isInternational)
    this.toggle(
      this.company_regTarget,
      this.company_reg_wrapperTarget,
      isInternational
    )

    // Australian entity fields
    this.toggle(this.abnTarget, this.abn_wrapperTarget, !isInternational)
    this.toggle(this.anzsicTarget, this.anzsic_wrapperTarget, !isInternational)
  }

  toggle(field, wrapper, show) {
    field.disabled = !show
    wrapper.style.display = show ? 'block' : 'none'
  }

  // On submit: block duplicate submissions. For v3, mint a fresh token (expires
  // ~2 minutes). For the v2 checkbox fallback, allow the browser's native submit
  // so `g-recaptcha-response` is posted reliably (programmatic form.submit()
  // has been a source of flaky checkbox verification).
  async guardSubmit(event) {
    if (this.submitting) {
      event.preventDefault()
      return
    }

    this.submitting = true
    const button = this.element.querySelector('button[type="submit"]')
    if (button) button.disabled = true

    // v2 checkbox challenge — do not preventDefault; let the native submit run.
    if (this.element.querySelector('.g-recaptcha')) return

    event.preventDefault()
    await this.refreshRecaptchaToken()
    // Native submit() doesn't re-fire the "submit" event, so this won't loop
    // back into guardSubmit. HTML5 validation already passed (the event only
    // fires post-validation), so not re-validating here is fine.
    this.element.submit()
  }

  // Replace the load-time reCAPTCHA token with a fresh one using the async
  // helper the `recaptcha_v3` view helper generates (it already embeds the site
  // key). Wait for `grecaptcha` first — Turbolinks can run this before api.js
  // finishes loading (Spiceworks 1029). Environments without the script
  // (dev/test) still submit; the server does the authoritative check.
  async refreshRecaptchaToken() {
    const execute = window.executeRecaptchaForCreateMemberAsync
    if (typeof execute !== 'function') return

    try {
      await this.waitForGrecaptcha(4000)
      const token = await this.withTimeout(execute(), 4000)
      if (!token) return
      const field = this.element.querySelector(
        '[name="g-recaptcha-response-data[create_member]"]'
      )
      if (field) field.value = token
    } catch (_) {
      // Timeout or script/network failure: fall through and submit with the
      // existing token rather than trapping the applicant on the page.
    }
  }

  waitForGrecaptcha(ms) {
    if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const started = Date.now()
      const timer = setInterval(() => {
        if (
          window.grecaptcha &&
          typeof window.grecaptcha.ready === 'function'
        ) {
          clearInterval(timer)
          resolve()
        } else if (Date.now() - started >= ms) {
          clearInterval(timer)
          reject(new Error('grecaptcha not loaded'))
        }
      }, 50)
    })
  }

  withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('recaptcha timeout')), ms)
      ),
    ])
  }

  connect() {
    this.submitting = false
    this.updateFields()
  }
}
